import { LoaderFunction, redirect } from 'react-router-dom';

// Graphql
import { client } from '../../../../gql/client';
import { FETCH_COMMUNICATIONS } from '../../../../gql/queries/communication';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';

// Selectors
import { selectCommunicationOrders } from '../../../../gql/selectors/communications';
import { store } from '../../../../state/configureStore';
import { getCursor } from '../../../../state/selectors/ui/tables';
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { GET_SELECTORS_DATA, GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_PREDEFINED_ORDERS } from '../../../../gql/queries/orders/predefinedData';
import { FETCH_USERS } from '../../../../gql/queries/users';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

export const HistoricalCommunicationsLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  if (userData?.user) {
    const dispatch = store.dispatch;
    const roleActive = userData.user.user.rolActive;
    const cursorCommunication = getCursor(
      tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS
    )(store.getState());

    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    let effectiveConcessionId = cursorCommunication.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, { ...cursorCommunication, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido. El inner try/catch ya cubre el throw mid-MISS.
    dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, false));

    const readCommunications = client.readQuery({
      query: FETCH_COMMUNICATIONS,
      variables: {
        input: {
          take: cursorCommunication.take,
          cursor: cursorCommunication.cursor,
          orderBy: cursorCommunication.orderBy,
          sortField: cursorCommunication.sortField,
          roadId: cursorCommunication.roadId,
          concessionId: effectiveConcessionId,
          interlocutorGroupId: cursorCommunication.interlocutorGroupId,
          fromDate: cursorCommunication.fromDate,
          toDate: cursorCommunication.toDate,
          indexType: cursorCommunication.indexType,
          indexExpedition: cursorCommunication.indexExpedition,
          classification: cursorCommunication?.classification,
          predefinedOrderId: cursorCommunication?.predefinedOrderId,
          states: cursorCommunication?.states,
          userIds: cursorCommunication?.userIds,
          pkInitKm: cursorCommunication?.pkInitKm,
          pkInitMeter: cursorCommunication?.pkInitMeter,
          pkEndKm: cursorCommunication?.pkEndKm,
          pkEndMeter: cursorCommunication?.pkEndMeter,
          description: cursorCommunication?.description,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorCommunication?.onlyFlagged,
        },
      },
    });

    const totalCount = readCommunications?.communications?.totalCount;
    const pageInfo = readCommunications?.communications?.pageInfo;

    const isCacheEmpty = JSON.stringify(readCommunications) === '{}';

    const ordersWithDataCommunication =
      readCommunications &&
      !isCacheEmpty &&
      (await selectCommunicationOrders(
        readCommunications?.communications?.communications
      ));
    const [filtersData, { data: { predefinedOrders } }, usersResult] = await Promise.all([
      client.query({
        query: GET_SELECTORS_DATA,
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const combinedData = {
      ordersWithDataCommunication,
      filtersData: {
        ...filtersData,
        data: {
          ...filtersData.data,
          users: usersResult.data?.users,
        },
      },
      predefinedOrders,
      totalCount,
      pageInfo,
      roleActive,
    };
    if (!readCommunications || isCacheEmpty) {
      dispatch(
        loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, true)
      );
      try {
        let communications;
        if (isCacheEmpty) {
          // Tarea #11: respetar el take guardado en cursor (page-size selector).
          const effectiveTake = firstPageTake(cursorCommunication.take, takeBase);
          communications = await client.query({
            query: FETCH_COMMUNICATIONS,
            variables: {
              input: {
                take: effectiveTake,
                cursor: '',
                orderBy: cursorCommunication.orderBy ?? 'desc',
                sortField: cursorCommunication.sortField,
                roadId: cursorCommunication.roadId,
                concessionId: effectiveConcessionId,
                interlocutorGroupId: cursorCommunication.interlocutorGroupId,
                fromDate: cursorCommunication.fromDate,
                toDate: cursorCommunication.toDate,
                indexType: cursorCommunication.indexType,
                indexExpedition: cursorCommunication.indexExpedition,
                classification: cursorCommunication?.classification,
                states: cursorCommunication?.states,
                userIds: cursorCommunication?.userIds,
                pkInitKm: cursorCommunication?.pkInitKm,
                pkInitMeter: cursorCommunication?.pkInitMeter,
                pkEndKm: cursorCommunication?.pkEndKm,
                pkEndMeter: cursorCommunication?.pkEndMeter,
                description: cursorCommunication?.description,
                predefinedOrderId: cursorCommunication?.predefinedOrderId,
                onlyFlagged: cursorCommunication?.onlyFlagged,
              },
            },
          });

          const setCursorData = {
            take: effectiveTake,
            cursor: '',
            roadId: cursorCommunication.roadId,
            concessionId: effectiveConcessionId,
            interlocutorGroupId: cursorCommunication.interlocutorGroupId,
            fromDate: cursorCommunication.fromDate,
            toDate: cursorCommunication.toDate,
            indexType: cursorCommunication.indexType,
            indexExpedition: cursorCommunication.indexExpedition,
            classification: cursorCommunication?.classification,
            predefinedOrderId: cursorCommunication?.predefinedOrderId,
            states: cursorCommunication?.states,
            userIds: cursorCommunication?.userIds,
            pkInitKm: cursorCommunication?.pkInitKm,
            pkInitMeter: cursorCommunication?.pkInitMeter,
            pkEndKm: cursorCommunication?.pkEndKm,
            pkEndMeter: cursorCommunication?.pkEndMeter,
            description: cursorCommunication?.description,
            onlyFlagged: cursorCommunication?.onlyFlagged,
          };

          dispatch(
            setCursor(
              tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS,
              setCursorData
            )
          );
        } else {
          communications = await client.query({
            query: FETCH_COMMUNICATIONS,
            variables: {
              input: {
                take: cursorCommunication.take,
                cursor: cursorCommunication.cursor,
                orderBy: cursorCommunication.orderBy,
                sortField: cursorCommunication.sortField,
                roadId: cursorCommunication.roadId,
                concessionId: effectiveConcessionId,
                interlocutorGroupId: cursorCommunication.interlocutorGroupId,
                fromDate: cursorCommunication.fromDate,
                toDate: cursorCommunication.toDate,
                indexType: cursorCommunication.indexType,
                indexExpedition: cursorCommunication.indexExpedition,
                classification: cursorCommunication?.classification,
                states: cursorCommunication?.states,
                userIds: cursorCommunication?.userIds,
                pkInitKm: cursorCommunication?.pkInitKm,
                pkInitMeter: cursorCommunication?.pkInitMeter,
                pkEndKm: cursorCommunication?.pkEndKm,
                pkEndMeter: cursorCommunication?.pkEndMeter,
                description: cursorCommunication?.description,
                predefinedOrderId: cursorCommunication?.predefinedOrderId,
                onlyFlagged: cursorCommunication?.onlyFlagged,
              },
            },
          });
        }

        const totalCount = communications.data.communications.totalCount;
        const pageInfo = communications.data.communications.pageInfo;
        const ordersWithDataCommunication = await selectCommunicationOrders(
          communications.data.communications.communications
        );
        const loading = communications.data.loading;
        const [filtersData, { data: { predefinedOrders } }, usersResult] = await Promise.all([
          client.query({
            query: GET_SELECTORS_DATA,
          }),
          client.query({
            query: FETCH_PREDEFINED_ORDERS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_USERS,
            fetchPolicy: 'cache-first',
          }),
        ]);

        const combinedData = {
          ordersWithDataCommunication,
          filtersData: {
            ...filtersData,
            data: {
              ...filtersData.data,
              users: usersResult.data?.users,
            },
          },
          predefinedOrders,
          totalCount,
          pageInfo,
          loading,
          roleActive,
        };

        dispatch(
          loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, false)
        );

        return combinedData;
      } catch (error) {
        console.error('[CommunicationsLoader] Error loading data:', error);
        dispatch(
          loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_COMMUNICATIONS, false)
        );
        return {
            ordersWithDataCommunication: [],
            filtersData: null,
            totalCount: 0,
            pageInfo: { hasNextPage: false, hasPreviousPage: false },
          };
      }
    }
    return combinedData;
  }
  return redirect('/login');
};

  return { data: load() };
};
